import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import {
  Filter,
  TableProvider,
  useParseFilter,
  useTable,
  useTablePagination,
  useTableSearch,
} from '@dfl/mui-admin-layout';
import { useSearchParamsChange } from '@dfl/react-security';
import { INTERVAL, useInterval } from 'components/libs/analytic/hooks/useInterval';
import { useToggle } from '@dfl/hook-utils';
import { Stack, StackProps } from '@mui/material';
import { PageHeader } from 'components/libs/PageHeader';
import { FilterFactory } from '@dofleini/query-builder';
import { useTranslation } from 'react-i18next';
import { TableHeaderOptions } from 'components/libs/table';
import TableToolbarActions from 'components/libs/table/toolbar/TableToolbarActions';
import { ChildrenProps } from '@dfl/mui-react-common';

// Data value of the provider context
type ContextValue = {
  interval?: INTERVAL;
  isReady: boolean;
};
// default value of the context
const defaultValue: ContextValue = {
  isReady: true,
};

// create context
const Context = createContext<ContextValue>(defaultValue);

// Proptypes of Provider component
type HeaderFilterContextProps = {
  id: string;
  intervalFilter?: string;
  defaultInterval?: INTERVAL;
  title?: string;
  filters?: Filter[];
  children: ReactNode | undefined;
  defaultValue?: string;
  showFilters?: boolean;
  defaultOrderField?: string;
  defaultOrder?: 'asc' | 'desc';
};

/**
 * Provider component
 * */
const HeaderFilterContext = ({
  intervalFilter,
  title,
  children,
  defaultValue,
  defaultInterval,
  showFilters = false,
  ...props
}: HeaderFilterContextProps) => {
  const { value, update } = useSearchParamsChange(intervalFilter);
  const [isReady, setIsReady] = useState(!intervalFilter);

  const resolvedValue = (value as string) || undefined;

  const interval = useInterval(resolvedValue, defaultInterval);

  useEffect(() => {
    if (!value && intervalFilter && defaultValue) {
      update({
        [intervalFilter]: defaultValue,
      });
    }
    setIsReady(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Context.Provider value={{ interval, isReady }} {...props}>
      <TableProvider {...props}>
        {showFilters && <HeaderFilterListToolbar title={title} />}
        {children}
      </TableProvider>
    </Context.Provider>
  );
};

// Default hook to retrieve context data
const useHeaderFilterContext = (): ContextValue => {
  const context = useContext(Context);
  if (context === undefined) {
    return defaultValue;
  }
  return context;
};

export { HeaderFilterContext, useHeaderFilterContext };

const useToolbarSetting = (activeMenu?: boolean, defaultFilterKeys?: string[]) => {
  const { isOpen, onClose, onOpen } = useToggle(false);
  const settings = useMemo<TableHeaderOptions>(() => {
    return {
      actions: {
        createAction: onOpen,
        create: false,
      },
      search: {
        disabled: true,
      },
      filter: {
        activeMenu,
        ...(defaultFilterKeys && { defaultFilterKeys }),
      },
    };
  }, [onOpen, activeMenu, defaultFilterKeys]);

  return {
    isOpen,
    onClose,
    settings,
  };
};
export const HeaderFilterListToolbar = ({
  title = 'dashboard',
  badge,
  activeMenu = true,
  defaultFilterKeys,
  children,
  subtitle,
  isLoading,
  ...props
}: { title?: string | ReactNode; badge?: ReactNode; activeMenu?: boolean; defaultFilterKeys?: string[]; subtitle?: ReactNode; isLoading?: boolean } & ChildrenProps & StackProps) => {
  const { settings } = useToolbarSetting(activeMenu, defaultFilterKeys);
  const { t } = useTranslation('report');

  return (
    <PageHeader mb={0} title={t(title)} badge={badge} subtitle={subtitle} isLoading={isLoading}>
      <Stack flexDirection={'row'} alignItems='center' gap={1} {...props}>
        <TableToolbarActions settings={settings} />
        {children}
      </Stack>
    </PageHeader>
  );
};

export const useHeaderTableFilter = () => {
  const { filters } = useTable();
  const { query: search } = useTableSearch();
  const urlFilterObj = useParseFilter(filters);
  const { page, rowsPerPage: size } = useTablePagination();

  return useMemo(() => {
    const filters = FilterFactory.factory(urlFilterObj);
    if (Object.keys(filters).length === 0) return {};
    return { filters, search, page, size };
  }, [page, search, size, urlFilterObj]);
};
